import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Button, BUTTON_THEME, Modal } from '@st-operations/uikit/src/v2'

import { getAudienceApiIntegrations } from '_services/api-integrations'

import './styles.scss'

// how each partner's answer reads
const STATES = {
  found: { label: 'There', className: 'is-found' },
  missing: { label: 'Not there', className: 'is-missing' },
  no_id: { label: 'No ID to look for', className: 'is-missing' },
  error: { label: "Couldn't check", className: 'is-error' },
}

// the modal's size and layout - it renders at the end of the page, outside #app
const MODAL_CLASSES = {
  content: 'ac-api-integrations-modal',
  innerWrapper: 'ac-api-integrations-modal__inner',
  childWrapper: 'ac-api-integrations-modal__body',
}

// "its global ID 0054cfaa"
const byId = ({ mapping_id: space, external_id: id }) => `its ${space} ID ${id}`

// The ID the partner knows the segment by: the one that found it, when other
// spaces' IDs were tried (Adelphic's "adelphic"), otherwise its distribution's
const segmentIdOf = (row) => row.found_by || row

// "Oct 8, 2026"
const day = (date) =>
  new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

// How this segment looks in each partner its taxonomy distributes to - one row
// per partner and mapping ID, its destinations listed together. A button for
// the page's header, beside Exit and Save, and the modal it opens.
//
// Asked live each time the modal opens - never on page load, since it calls
// every partner. Which partners, and which ID each knows the segment by, come
// from the taxonomy's distributions: the same pairing as Segment IDs above,
// done on the server. Beside each answer is what our own pushes recorded, so
// a name changed over there since shows up.
const ApiIntegrations = ({ audience, className }) => {
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  // an answer from an earlier opening, landing late, is dropped
  const latest = useRef(0)

  const show = async () => {
    latest.current += 1
    const ticket = latest.current
    setOpen(true)
    setResult(null)
    setError(null)
    try {
      const data = await getAudienceApiIntegrations(audience._id)
      if (ticket === latest.current) setResult(data)
    } catch (e) {
      if (ticket === latest.current) setError(e?.message || 'could not ask the partners')
    }
  }

  const integrations = result?.integrations || []

  return (
    <>
      <Button
        theme={BUTTON_THEME.SECONDARY}
        onClick={show}
        className={className}
        title="How this segment looks in each partner its taxonomy distributes to"
      >
        API Integrations
      </Button>

      <Modal isOpen={open} onClose={() => setOpen(false)} classes={MODAL_CLASSES}>
        <section className="ac-api-integrations-modal__content">
          <div className="ac-api-integrations-modal__head">
            <h2 className="ac-api-integrations-modal__title">API Integrations</h2>
            <div className="ac-api-integrations-modal__meta">{audience.name}</div>
          </div>

          <Choose>
            <When condition={error}>
              <div className="ac-api-integrations-modal__error">{error}</div>
            </When>
            <When condition={!result}>
              <div className="ac-api-integrations-modal__note">Asking each partner…</div>
            </When>
            <When condition={!integrations.length}>
              <div className="ac-api-integrations-modal__note">
                None of this taxonomy&apos;s distributions go to a partner we have an API
                integration for.
              </div>
            </When>
            <Otherwise>
              <div className="ac-api-integrations-modal__table">
                <table className="ac-api-integrations-table">
                  <thead>
                    <tr>
                      <th>Partner</th>
                      <th>Segment ID</th>
                      <th>In their system</th>
                      <th>Name there</th>
                      <th>Folder</th>
                      <th>Rate</th>
                      <th>Last push</th>
                    </tr>
                  </thead>
                  <tbody>
                    <For each="row" of={integrations}>
                      <tr key={`${row.partner.slug}-${row.mapping_id}-${row.external_id}`}>
                        <td>
                          <div className="is-strong">{row.partner.name}</div>
                          <div className="is-quiet">
                            {row.destinations.map((d) => d.name).join(', ')}
                          </div>
                        </td>
                        <td>
                          <div className="is-mono">{segmentIdOf(row).external_id || '-'}</div>
                          <div className="is-quiet">{segmentIdOf(row).mapping_id}</div>
                        </td>
                        <td>
                          <span
                            className={`ac-api-integrations-table__state ${
                              STATES[row.state]?.className || ''
                            }`}
                          >
                            {STATES[row.state]?.label || row.state}
                          </span>
                          {/* looked up by other spaces' IDs - Adelphic's "adelphic" */}
                          <If condition={row.state === 'missing' && row.looked_up}>
                            <div className="is-quiet">
                              looked for {row.looked_up.map(byId).join(', then ')}
                            </div>
                          </If>
                          {/* AppNexus and Adelphic number their own */}
                          <If condition={row.id && row.id !== segmentIdOf(row).external_id}>
                            <div className="is-quiet">their ID {row.id}</div>
                          </If>
                          <If condition={row.status}>
                            <div className="is-warn">{row.status}</div>
                          </If>
                          <If condition={row.error}>
                            <div className="is-bad">{row.error}</div>
                          </If>
                        </td>
                        <td>
                          {row.name || '-'}
                          {/* renamed over there since our last push */}
                          <If
                            condition={
                              row.name &&
                              row.recorded?.name_sent &&
                              row.recorded.name_sent !== row.name
                            }
                          >
                            <div className="is-warn">we last sent “{row.recorded.name_sent}”</div>
                          </If>
                        </td>
                        <td>{row.folder?.name || '-'}</td>
                        <td>
                          {row.rate || <span className="is-quiet">{row.rate_note || '-'}</span>}
                        </td>
                        <td>
                          <Choose>
                            <When condition={row.recorded?.last_push}>
                              <Link to={`/taxonomy-api-integrations/${row.recorded.last_push}`}>
                                {row.recorded.last_synced_at
                                  ? day(row.recorded.last_synced_at)
                                  : 'open it'}
                              </Link>
                            </When>
                            <Otherwise>
                              <span className="is-quiet">-</span>
                            </Otherwise>
                          </Choose>
                        </td>
                      </tr>
                    </For>
                  </tbody>
                </table>
              </div>
            </Otherwise>
          </Choose>
        </section>
      </Modal>
    </>
  )
}

ApiIntegrations.propTypes = {
  audience: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  // the header's, so it sits like Exit and Save
  className: PropTypes.string,
}

ApiIntegrations.defaultProps = {
  className: undefined,
}

export default ApiIntegrations
